/* eslint-disable */
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {fakeDelay, getAuthorizationHeader} from "@/utils";
import {makeRequired, makeValidate} from "mui-rff";
import {schema} from "@/features/user/cfdiBackground/validations";
import {makeYupTranslatedError} from "@/helpers/yup";
import {TLogoForm} from "@/features/user/cfdiBackground/types";
import LogoForm from "@/features/user/cfdiBackground/components/form";
import {Box, MenuItem} from "@mui/material";
import {handleReadBase64} from "@/utils/filepond";
import {toastError, toastSuccess} from "@/helpers/toast";
import Page from "@/components/page";
import {getBackground, registerBackground} from "@/apis/register/background";
import {checkErrorResponse} from "@/helpers/axios";

const initialData: any = {
    backgroundName: '',
    backgroundBase64: '',
}

const UserCFDIBGComponent = ({isModal, closePopover, from = "Route"}: any): any => {
    //console.log("@UserCFDIBGComponent loading component from ", from);

    const {t} = useTranslation();

    const [loading, setLoading] = useState(true);

    // to know if modal is open
    const [modalOpen, setModalOpen] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    const dataRef = useRef(initialData);

    const [logo, setLogo] = useState(null);
    const [logoName, setLogoName] = useState(null);
    const [showLogo, setShowLogo] = useState(false);
    const [uris, setUris] = useState([]);

    const [filesLoaded, setFilesLoaded] = useState({
        backgroundBase64: false
    });

    const pondInstances: any[] = [];
    const pondInstancesRef = useRef(pondInstances);

    const validateForm = makeValidate(schema, makeYupTranslatedError);

    const required = makeRequired(schema);

    /*
    *
    * FILEPONDS
    *
    * */

    const onInit = (pond: any) => {
        // creamos un nuevo array de intancias de ponds
        let newPondInstances: any[] = [];
        newPondInstances = newPondInstances.concat(pondInstancesRef.current, pond);

        pondInstancesRef.current = newPondInstances;
    };

    const onAddFile = async (file: any, pondInstance: any = null) => {
        // leer el file
        const base64Text = await handleReadBase64(file);
        dataRef.current = {...dataRef.current, backgroundBase64: base64Text, backgroundName: file.name};
        setFilesLoaded({...filesLoaded, backgroundBase64: true});
    }

    const resetFileState = (pondInstance: any = null) => {
        dataRef.current = {...dataRef.current, backgroundBase64: null, backgroundName: null}
        setFilesLoaded({...filesLoaded, backgroundBase64: true});
        if (pondInstance) {
            // truco para evitar un loop del state
            setTimeout(() => pondInstance.removeFiles(), 50);
        }
    }

    const onRemoveFile = () => {
        // truco para evitar loop en el render
        setTimeout(() => resetFileState(), 50);
    }

    const handleUpload = () => {
        pondInstancesRef.current.forEach((pondInstance: any) => {
            if (pondInstance.getFiles().length > 0) {
                pondInstance.processFiles();
            }
        });
    }

    /*
    *
    * FORMS
    *
    * */

    const onProcessCompleteWrapper = () => {
        const payload = dataRef.current;
        submitData(payload).catch((e) => console.error(e));
    };

    const onSubmit = async (values: any, form: any) => {
        // restart form validations
        form.reset();
        form.restart();

        setSubmitting(true);

        await fakeDelay(50)
        handleUpload();
    }

    const submitData = useCallback(async (payload: any) => {
        //console.log('payload', payload);

        try {
            const response = await registerBackground(payload);
            //console.log(response);
            setLogo(response.data.background_uri);
            setLogoName(response.data.background_name);
            setDownloadButton(response.data.download_params);
            toastSuccess(response.data.message);
            setShowLogo(true);
        } catch (e) {
            setLogo(null)
            setLogoName(null);
            setShowLogo(false);
            toastError(t('common.api_error'));
            checkErrorResponse(e);
        } finally {
            setSubmitting(false);
            resetFileState(pondInstancesRef.current[0]);
        }

        return true;
    }, []);

    const ponds: any[] = [
        {
            // general config
            label: `cfdi_bg.label_logo`,
            requiredFile: true,
            name: 'backgroundBase64',
            translateLabels: true,
            maxFiles: 1, // null = todos los que quieras
            allowMultiple: false,
            instantUpload: false,

            // validation
            allowFileTypeValidation: true,
            acceptedFileTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
            fileValidateTypeLabelExpectedTypesMap: {'image/png': 'png', 'image/jpeg': 'jpeg', 'image/jpg': 'jpg', 'image/gif': 'gif'},
            allowFileSizeValidation: true,
            maxFileSize: '2MB',

            // buttons
            hasUploadButton: false,
            hasResetButton: false,
            showDownloadFile: false,
            hasDownloadFile: false,

            // parent executions
            onInit,
            onAddFile,
            onProcessCompleteWrapper,
            onRemoveFile,

            // base 64
            isBase64: true,

            authorization: getAuthorizationHeader(),

            formFieldName: 'backgroundBase64',
        },
    ];

    const downloadProps: any = {
        uris: uris,
        zipName: 'archivo',
        url: 'api/auth/download/background',
        component: null
    }

    const setDownloadButton = (download_params: any) => {
        const uri = download_params;
        uri.url = 'api/auth/download/background';
        uri.zipName = 'archivos'
        setUris([uri]);
    }

    const formProps: any = {
        onSubmit,
        initialValues: dataRef.current,
        validateForm,
        required,
        submitting,
        ponds,
        isModal,
        logo,
        showLogo,
        closePopover,
        logoName,
        downloadProps,
        setModalOpen,
        loading
    }

    const getData = useCallback(async () => {
        // set loader
        setLoading(true);

        try {
            const response = await getBackground();
            setLogo(response.data.background_uri);
            setShowLogo(true);
            setLogoName(response.data.background_name);
            setDownloadButton(response.data.download_params);
        } catch (e) {
            console.error('error getting logo', e);
            //checkErrorResponse(e);
        }

        setLoading(false);
    }, []);

    useEffect(() => {
        if (!isModal)
            getData().catch((e) => console.error(e));
    }, []);

    useEffect(() => {
        //console.log("@UserCFDIBGComponent sensing value modalOpen: ", modalOpen);
        if (modalOpen) {
            //console.log("@UserCFDIBGComponent modal is open and not loaded data first time");
            getData().catch((e: any) => console.error(e));
        }
    }, [modalOpen])

    return (
        <Page
            title={(!isModal) && t("cfdi_bg.modal_title")}
        >
            <Box
                sx={{
                    ...(!isModal && {
                        mt: 2,
                    }),
                }}
            >
                {/*{(loading && !isModal) && <>{t('common.loading')}</>}*/}

                {/*{(loading && isModal) && <MenuItem>{t('cfdi_bg.title')}</MenuItem>}*/}

                {<LogoForm {...formProps} />}
            </Box>
        </Page>
    );
}

export default UserCFDIBGComponent;
